import { ArrowDownIcon } from "@chakra-ui/icons"
import type { BoxProps, ComponentWithAs, IconProps } from "@chakra-ui/react"
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Input,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react"
import { BigNumber as BigNumberEthers } from "@ethersproject/bignumber"
import { parseUnits } from "ethers/lib/utils.js"
import { Formik, useFormik } from "formik"
import { type FC, useEffect, useMemo, useState } from "react"
import { useContractRead } from "wagmi"
import * as Yup from "yup"
import CardContainer from "@/common/components/CardContainer"
import { ROUTES } from "@/common/constants/routes"
import { limitNumberDecimals } from "@/common/helpers/number"
import { useModals } from "@/common/hooks/useModals"
import GovernanceAvatar from "@/governance/components/GovernanceAvatar"
import { type Organization, useAddressDelegatingToQuery } from "@/query/graphql"
import { getTokenWrapperSettings } from "@/token/constants/token-wrapper"
import { useTokenWrapperTransaction } from "@/token/hooks/useTokenWrapperTransaction"
import type { TokenBalanceResult } from "@/token/types/TokenBalance"
import { ArrowRight } from "@/ui/components/icons/font-awesome/ArrowRight"
import { ArrowUpArrowDown } from "@/ui/components/icons/font-awesome/ArrowUpArrowDown"
import { CoinVertical } from "@/ui/components/icons/font-awesome/CoinVertical"
import NavigationTabs from "@/ui/components/NavigationTabs"
import { useMe } from "@/user/providers/MeProvider"
import Web3StatusModal from "@/web3/components/Web3StatusModal"
import { isSameAddress } from "@/web3/helpers/address"
import { getChainIdParams } from "@/web3/helpers/chainId"
import { getSepoliaChainId } from "@/web3/helpers/chainReference"
import { useAddress } from "@/web3/hooks/useAddress"
import { useMultichainAccount } from "@/web3/hooks/useMultichainAccount"
import { useSignerStore } from "@/web3/providers/SignerProvider"
import { TransactionState } from "@/web3/types/transaction"

type Props = {
  organization: Organization
  unwrapBalance?: TokenBalanceResult
  governanceBalance?: TokenBalanceResult
  type: "wrap" | "unwrap"
}
const TokenWrapperContent: FC<Props> = ({
  organization,
  type,
  unwrapBalance,
  governanceBalance,
}) => {
  const tabs = [
    {
      label: "Wrap",
      url: ROUTES.governance.token.wrap(organization.slug),
      isActive: type === "wrap",
    },
    {
      label: "Unwrap",
      url: ROUTES.governance.token.unwrap(organization.slug),
      isActive: type === "unwrap",
    },
  ]

  return (
    <Stack direction="column" spacing={1}>
      <NavigationTabs
        useTabs
        borderBottomRadius={0}
        borderBottomWidth={0}
        mb="-4px"
        tabs={tabs}
      />
      <CardContainer
        borderTop={0}
        borderTopRadius={0}
        pt={{ base: 4, md: "1" }}
      >
        <Formik
          initialValues={{
            from: "",
            to: "",
          }}
          onSubmit={() => {
            return
          }}
        >
          {(properties) => {
            return (
              <TokenWrapFormInput
                governanceBalance={governanceBalance}
                organization={organization}
                type={type}
                unwrapBalance={unwrapBalance}
                {...properties}
              />
            )
          }}
        </Formik>
      </CardContainer>
    </Stack>
  )
}

type TokenWrapperValues = {
  fromValue: number | string
  toValue: number | string
}

type TxStep = {
  state: TransactionState
  message: string
  icon: ComponentWithAs<"svg", IconProps>
}

type TokenWrapFormInputProps = {
  type: "wrap" | "unwrap"
  governanceBalance?: TokenBalanceResult
  unwrapBalance?: TokenBalanceResult
  organization: Organization
}
const TokenWrapFormInput: FC<TokenWrapFormInputProps> = ({
  type,
  governanceBalance,
  unwrapBalance,
  organization,
}) => {
  const { signer } = useSignerStore()
  const [action, setAction] = useState<"wrap" | "unwrap" | "approve">()
  const chainId = getSepoliaChainId()
  const { reference } = getChainIdParams(chainId)
  const tokenWrapperSettings = getTokenWrapperSettings(organization.id)
  const me = useMe()

  const address = useAddress()

  const [txHash, setTxHash] = useState<string | undefined>(undefined)
  const [txSteps, setTxSteps] = useState<TxStep[]>([])

  const { web3StatusModal } = useModals()
  const { multichainAccount: walletAccount } = useMultichainAccount()
  const canUseSafeTx =
    me?.type === "SAFE" && !isSameAddress(me.address, walletAccount.address)

  // TODO(@nicolas): Move to a web3 hook?
  const { data: allowanceData } = useContractRead({
    address: tokenWrapperSettings.underlyingToken
      .contractAddress as `0x${string}`,
    abi: [
      {
        inputs: [
          { internalType: "address", name: "owner", type: "address" },
          { internalType: "address", name: "spender", type: "address" },
        ],
        name: "allowance",
        outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
        stateMutability: "view",
        type: "function",
      },
    ],
    functionName: "allowance",
    args: [
      address as `0x${string}`,
      tokenWrapperSettings.votesToken.contractAddress as `0x${string}`,
    ],
    enabled: type === "wrap",
    watch: true,
  })

  // Allowance is the amount of tokens that the contract is allowed to spend on behalf of the user
  const allowance = allowanceData?.toString() ?? "0"

  const tokenId = organization.tokenIds[0]

  const { depositFor, withdrawTo, approve, status, safeTxHash } =
    useTokenWrapperTransaction()

  const unwrapBalanceNumber = unwrapBalance?.formatted
    ? limitNumberDecimals(Number(unwrapBalance.formatted), 2)
    : 0
  const governanceBalanceNumber = governanceBalance?.formatted
    ? limitNumberDecimals(Number(governanceBalance.formatted), 2)
    : 0

  const { fromValueMax, fromSymbol, toSymbol, fromDecimals } = useMemo(() => {
    if (type === "wrap") {
      return {
        fromValueMax: unwrapBalanceNumber,
        fromSymbol: unwrapBalance?.symbol,
        toSymbol: governanceBalance?.symbol,
        fromDecimals: unwrapBalance?.decimals ?? 18,
      }
    }

    return {
      fromValueMax: governanceBalanceNumber,
      fromSymbol: governanceBalance?.symbol,
      toSymbol: unwrapBalance?.symbol,
      fromDecimals: governanceBalance?.decimals ?? 18,
    }
  }, [
    type,
    governanceBalanceNumber,
    unwrapBalanceNumber,
    governanceBalance?.symbol,
    governanceBalance?.decimals,
    unwrapBalance?.symbol,
    unwrapBalance?.decimals,
  ])

  // Find if the account is already delegating votes
  const { data } = useAddressDelegatingToQuery(
    {
      delegateeInput: {
        address: address ?? "",
        tokenId: tokenId ?? "",
      },
    },
    {
      enabled: Boolean(address) && Boolean(tokenId),
    },
  )
  const hasVotingPowerDelegated = Boolean(data?.delegatee?.delegate)
  // biome-ignore lint: biome-migration
  useEffect(() => {
    initTxSteps()
  }, [])

  const initTxSteps = () => {
    if (type === "wrap") {
      setTxSteps([
        {
          state: acceptTxIsRequired
            ? canUseSafeTx
              ? TransactionState.Begin
              : TransactionState.None
            : TransactionState.Success,
          message: "Approve",
          icon: CoinVertical,
        },
        {
          state: acceptTxIsRequired
            ? TransactionState.None
            : canUseSafeTx
              ? TransactionState.Begin
              : TransactionState.None,
          message: "Wrap",
          icon: ArrowUpArrowDown,
        },
      ])
    }

    if (type === "unwrap") {
      setTxSteps([
        {
          state: canUseSafeTx ? TransactionState.Begin : TransactionState.None,
          message: "Unwrap",
          icon: ArrowUpArrowDown,
        },
      ])
    }

    if (txHash !== undefined) {
      setTxHash(undefined)
    }
  }

  const onSubmit = () => {
    if (!isValid) return

    if (txSteps.length === 0) return

    if (type === "wrap") {
      if (!web3StatusModal.isOpen) {
        initTxSteps()
      }

      // If approve tx is required, then call approve first
      if (acceptTxIsRequired) {
        handleApprove()
      }

      // If approve tx is not required, then call depositFor
      else {
        handleDepositFor()
      }
    }

    if (type === "unwrap") {
      handleWithdrawTo()

      return
    }
  }

  // Handle approve tx
  const handleApprove = () => {
    if (!isValid) return

    setAction("approve")

    approve({
      signer,
      amount: parseUnits(values.fromValue.toString(), fromDecimals),
      address: tokenWrapperSettings.votesToken.contractAddress,
      tokenContractAddress:
        tokenWrapperSettings.underlyingToken.contractAddress,
      chainId: tokenWrapperSettings.underlyingToken.chainId,
    })

    if (!web3StatusModal.isOpen) {
      web3StatusModal.onOpen()
    }

    return
  }

  // Handle depositorFor (Wrap) tx
  const handleDepositFor = () => {
    if (!isValid) return

    setAction("wrap")

    const optimisticBalance =
      governanceBalanceNumber + +values?.fromValue?.toString()

    depositFor({
      optimisticBalance,
      signer,
      amount: parseUnits(values.fromValue.toString(), fromDecimals),
      address: address as string,
      tokenContractAddress: tokenWrapperSettings.votesToken.contractAddress,
      chainId: tokenWrapperSettings.votesToken.chainId,
    })

    if (!web3StatusModal.isOpen) {
      web3StatusModal.onOpen()
    }

    return
  }

  // Handle withdrawTo (Unwrap) tx
  const handleWithdrawTo = () => {
    if (!isValid) return

    setAction("unwrap")

    const optimisticBalance =
      governanceBalanceNumber - +values?.fromValue?.toString()

    withdrawTo({
      optimisticBalance,
      signer,
      amount: parseUnits(values.fromValue.toString(), fromDecimals),
      address: address as string,
      tokenContractAddress: tokenWrapperSettings.votesToken.contractAddress,
      chainId: tokenWrapperSettings.votesToken.chainId,
    })

    if (!web3StatusModal.isOpen) {
      web3StatusModal.onOpen()
    }
  }

  const { errors, values, isValid, handleSubmit, setFieldValue, resetForm } =
    useFormik<TokenWrapperValues>({
      onSubmit,
      initialValues: {
        fromValue: "",
        toValue: "",
      },
      validationSchema: Yup.object({
        fromValue: Yup.number()
          .required("Required field")
          .typeError("Must be a number")
          .max(fromValueMax, "Exceeds available balance")
          .test(
            "greaterThanZero",
            "Enter an amount",
            (value) => (value as number) > 0,
          ),
        toValue: Yup.number()
          .required("Required field")
          .typeError("Must be a number")
          .min(0, "Enter an amount"),
      }),
    })

  // Check if the user needs to approve the contract to spend their tokens
  const acceptTxIsRequired = useMemo(() => {
    if (type === "wrap") {
      if (allowance === "0") {
        return true
      }

      try {
        if (values.fromValue !== "") {
          const inputValue = parseUnits(
            values.fromValue.toString(),
            fromDecimals,
          )

          if (BigNumberEthers.from(inputValue.toString()).lte(allowance)) {
            return false
          }

          return true
        }
      } catch {
        return true
      }
    }

    return true
  }, [allowance, fromDecimals, type, values.fromValue])
  // biome-ignore lint: biome-migration
  useEffect(() => {
    if (values.fromValue !== values.toValue) {
      setFieldValue("toValue", values.fromValue)
    }
  }, [values.fromValue, setFieldValue])
  // biome-ignore lint: biome-migration
  useEffect(() => {
    if (values.toValue !== values.fromValue) {
      setFieldValue("fromValue", values.toValue)
    }
  }, [values.toValue, setFieldValue])

  const handleChangeField = (
    event: any,
    setFieldValue: any,
    fieldName: string,
  ) => {
    const { value } = event.target
    const formattedValue = value.replace(/^0+/, "") || "0"
    setFieldValue(fieldName, formattedValue)
  }

  const isButtonEnabled = isValid && !!values.fromValue
  // biome-ignore lint: biome-migration
  useEffect(() => {
    if (txSteps.length === 0) return

    if (type === "wrap") {
      // Approve processing
      if (
        txSteps[0].state !== TransactionState.Success &&
        txSteps[0].state !== TransactionState.PendingSafe &&
        !(
          txSteps[0].state === TransactionState.Begin &&
          status.state === TransactionState.None
        ) &&
        !(
          txSteps[0].state === TransactionState.Fail &&
          status.state === TransactionState.None
        )
      ) {
        setTxSteps((prevTxSteps) => [
          {
            ...prevTxSteps[0],
            state: status.state,
          },
          ...prevTxSteps.slice(1),
        ])

        if (status.state === TransactionState.Success) {
          handleDepositFor()
        }

        return
      }

      // Wrap processing
      if (
        txSteps[1].state !== TransactionState.Success &&
        txSteps[1].state !== TransactionState.PendingSafe &&
        !(
          txSteps[1].state === TransactionState.Begin &&
          status.state === TransactionState.None
        ) &&
        !(
          txSteps[1].state === TransactionState.Fail &&
          status.state === TransactionState.None
        )
      ) {
        setTxSteps((prevTxSteps) => [
          ...prevTxSteps.slice(0, 1),
          {
            ...prevTxSteps[1],
            state: status.state,
          },
        ])

        if (status.state === TransactionState.Success) {
          setTxHash(status.transaction.hash)
        }
      }
    }

    // Unwrap processing
    if (type === "unwrap") {
      if (
        txSteps[0].state !== TransactionState.Success &&
        txSteps[0].state !== TransactionState.PendingSafe &&
        !(
          txSteps[0].state === TransactionState.Begin &&
          status.state === TransactionState.None
        ) &&
        !(
          txSteps[0].state === TransactionState.Fail &&
          status.state === TransactionState.None
        )
      ) {
        setTxSteps((prevTxSteps) => [
          {
            ...prevTxSteps[0],
            state: status.state,
          },
        ])

        if (status.state === TransactionState.Success) {
          setTxHash(status.transaction.hash)
        }
      }

      return
    }
  }, [status])
  // biome-ignore lint: biome-migration
  useEffect(() => {
    // Closing the modal after the transaction is successful should reset the form
    if (!web3StatusModal.isOpen && txHash) {
      resetForm()
    }
  }, [web3StatusModal.isOpen])

  return (
    <>
      <form style={{ width: "100%" }} onSubmit={handleSubmit}>
        <Stack align="flex-start" color="gray.700" spacing="4px" width="100%">
          <FormControl isInvalid={Boolean(errors?.["fromValue"])} width="100%">
            <Stack
              border={
                Boolean(errors?.["fromValue"]) ? "2px solid red" : "gray.200"
              }
              borderRadius="md"
              borderWidth="1px"
              px={3}
              py={4}
              spacing={0}
            >
              <Text>{fromSymbol} amount</Text>
              <Input
                _focus={{ borderColor: "currentColor" }}
                _invalid={{ borderColor: "red.500" }}
                border="none"
                borderColor={
                  Boolean(errors?.["fromValue"]) ? "initial" : "currentColor"
                }
                focusBorderColor="transparent"
                fontSize="4xl"
                fontWeight="bold"
                p={0}
                placeholder="0"
                sx={{
                  "::placeholder": {
                    color: "gray.400",
                  },
                }}
                textAlign="left"
                value={values.fromValue}
                onChange={(e: any) =>
                  handleChangeField(e, setFieldValue, "fromValue")
                }
              />

              <Flex mr={4} mt="14px" position="absolute" right={0} zIndex={600}>
                <Button
                  bgColor="purple.100"
                  borderRadius="md"
                  color="purple.600"
                  fontWeight="bold"
                  onClick={() => setFieldValue("fromValue", fromValueMax)}
                >
                  MAX
                </Button>
              </Flex>
            </Stack>

            <FormErrorMessage>{errors.fromValue}</FormErrorMessage>
          </FormControl>

          <SwapIcon icon={ArrowDownIcon} width="100%" />

          <FormControl width="100%">
            <Stack
              borderColor="gray.200"
              borderRadius="md"
              borderWidth="1px"
              px={3}
              py={4}
              spacing={0}
            >
              <Text>{toSymbol} amount</Text>

              <Input
                _focus={{ borderColor: "currentColor" }}
                _invalid={{ borderColor: "red.500" }}
                border="none"
                borderColor={
                  Boolean(errors?.["toValue"]) ? "initial" : "currentColor"
                }
                focusBorderColor="transparent"
                fontSize="4xl"
                fontWeight="bold"
                p={0}
                placeholder="0"
                sx={{
                  "::placeholder": {
                    color: "gray.400",
                  },
                }}
                textAlign="left"
                value={values.toValue}
                onChange={(e: any) =>
                  handleChangeField(e, setFieldValue, "toValue")
                }
              />
            </Stack>
          </FormControl>

          <Flex pt={2} width="100%">
            <Button
              isDisabled={!isButtonEnabled}
              type="submit"
              variant="primary"
              width="100%"
            >
              {type === "wrap" ? "Wrap" : "Unwrap"}
            </Button>
          </Flex>
        </Stack>
      </form>

      <Web3StatusModal
        body={
          <Stack align="center" direction="row" justify="center" width="100%">
            <GovernanceAvatar size={6} src={organization?.metadata?.icon} />
            <Text color="gray.600" fontSize="md" fontWeight="regular">
              {values.fromValue} {fromSymbol}
            </Text>
            <SwapIcon icon={ArrowRight} size={4} width="40px" />
            <Text color="gray.600" fontSize="md" fontWeight="regular">
              {values.toValue} {toSymbol}
            </Text>
          </Stack>
        }
        chainReference={reference}
        footer={{
          [TransactionState.Success]:
            action === "wrap" && !hasVotingPowerDelegated ? (
              <Stack align="center" width="100%">
                <Divider />
                <Text color="gray.600" fontSize="md" mt={4} textAlign="center">
                  Your tokens must be delegated before they can used to
                  participate in governance
                </Text>
                <Stack mt={2} width="100%">
                  <Link
                    href={ROUTES.governance.myVotingPower.index(
                      organization.slug,
                    )}
                    width="100%"
                  >
                    <Button variant="primary" width="100%">
                      Delegate now
                    </Button>
                  </Link>
                  <Button
                    variant="secondary"
                    width="100%"
                    onClick={web3StatusModal.onClose}
                  >
                    Skip for now
                  </Button>
                </Stack>
              </Stack>
            ) : null,
        }}
        safeTxHash={safeTxHash}
        status={status}
        txHash={txHash}
        txSteps={txSteps}
      />
    </>
  )
}

type SwapIconProps = {
  icon: ComponentWithAs<"svg", IconProps>
  size?: number
}
export const SwapIcon: FC<SwapIconProps & BoxProps> = ({
  icon,
  size = 6,
  ...boxProps
}) => {
  return (
    <Box position="relative" zIndex={900} {...boxProps}>
      <Flex
        align="center"
        bg="white"
        border="gray.200"
        borderRadius="full"
        borderWidth="2px"
        h={size * 2}
        justify="center"
        left="50%"
        position="absolute"
        transform="translate(-50%, -50%)"
        w={size * 2}
      >
        <Icon as={icon} h={size} w={size} />
      </Flex>
    </Box>
  )
}

export default TokenWrapperContent
